import { useEffect, useState } from 'react';
import { API_ROUTES, type Uuid } from '@zenda/api-contract';
import { getToken } from './api';

/**
 * Busca o anexo COM autenticação e devolve uma URL local.
 *
 * ── Por que não colocar a URL direto no `<img src>` ──
 * `<img>`, `<audio>` e `<a download>` não mandam o header `Authorization` — só
 * cookies. A rota do anexo é autenticada por Bearer, então um `src` apontando
 * para ela receberia 401 e a prévia apareceria quebrada.
 *
 * A saída óbvia seria aceitar o token na query string. Não: token em URL vai
 * parar em log de acesso, em histórico e em `Referer`, e este dá acesso a
 * conversa de paciente. Buscar com `fetch` mantém o token no header, e o
 * `blob:` que sai daqui vale só para esta aba.
 *
 * O `revoke` na limpeza não é higiene opcional: cada blob segura o arquivo
 * inteiro na memória do navegador até ser revogado, e uma conversa com vinte
 * fotos seguraria as vinte.
 */
export function useMidia(
  messageId: Uuid,
  ativo: boolean,
): {
  readonly url: string | null;
  readonly carregando: boolean;
  readonly erro: string | null;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!ativo) return;

    let vivo = true;
    let criada: string | null = null;
    setCarregando(true);
    setErro(null);

    void (async () => {
      try {
        const token = getToken();
        const r = await fetch(API_ROUTES.messageMedia(messageId), {
          headers: token === null ? {} : { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          const j: unknown = await r.json().catch(() => ({}));
          throw new Error((j as { message?: string }).message ?? 'Falha ao abrir o anexo.');
        }
        const blob = await r.blob();
        if (!vivo) return;
        criada = URL.createObjectURL(blob);
        setUrl(criada);
      } catch (e) {
        if (vivo) setErro(e instanceof Error ? e.message : 'Falha ao abrir o anexo.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();

    return () => {
      vivo = false;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [messageId, ativo]);

  return { url, carregando, erro };
}

/**
 * Salva o anexo no computador.
 *
 * Mesmo motivo do hook: o `download` de um `<a>` não carrega o header. E é esta
 * a resposta ao prazo — o arquivo expira na Meta, e quem precisa guardar guarda
 * aqui.
 */
export async function baixarAnexo(messageId: Uuid, filename: string | null): Promise<void> {
  const token = getToken();
  const r = await fetch(API_ROUTES.messageMedia(messageId, true), {
    headers: token === null ? {} : { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error('Não foi possível baixar o anexo.');

  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `anexo-${messageId}`;
  a.click();
  URL.revokeObjectURL(url);
}
